import React, { useEffect, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import formula1Service from '../services/formula1Service'
import futureChampionService from '../services/futureChampionService'
import { Formula1Prediction, Formula1Race, Formula1PlayerPoints } from '../types/formula1'
import { FutureChampionEvent, FutureChampionPrediction } from '../types/futureChampion'
import Formula1PredictedGrid from './Formula1PredictedGrid'
import toast from 'react-hot-toast'

interface ViewPlayerFormula1StatsModalProps {
  player: {
    _id: string
    id?: string
    name: string
    email?: string
    avatar?: string
  }
  playerId: string
  roomId: string
  isOpen: boolean
  onClose: () => void
  viewType: 'stats' | 'games'
}

const ViewPlayerFormula1StatsModal: React.FC<ViewPlayerFormula1StatsModalProps> = ({
  player,
  playerId,
  roomId,
  isOpen,
  onClose,
  viewType,
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(true)
  const [predictions, setPredictions] = useState<Formula1Prediction[]>([])
  const [races, setRaces] = useState<Record<string, Formula1Race>>({})
  const [futureChampionEvents, setFutureChampionEvents] = useState<FutureChampionEvent[]>([])
  const [futureChampionPredictions, setFutureChampionPredictions] = useState<FutureChampionPrediction[]>([])
  const [loadingFutureChampion, setLoadingFutureChampion] = useState(false)
  const [playerStats, setPlayerStats] = useState<Formula1PlayerPoints | null>(null)

  const eventOrder = (name: string) => {
    if (!name) return 99
    const n = name.toLowerCase()
    if (n.includes('start')) return 1
    if (n.includes('second')) return 2
    if (n.includes('third')) return 3
    return 99
  }

  const orderedEvents = useMemo(() => {
    return [...futureChampionEvents].sort((a, b) => eventOrder(a.name) - eventOrder(b.name))
  }, [futureChampionEvents])

  const slotsToShow = useMemo(() => orderedEvents.slice(0, 3), [orderedEvents])

  const predictionByEventId = useMemo(() => {
    const map: Record<string, FutureChampionPrediction> = {}
    futureChampionPredictions.forEach((p) => {
      const eid = p.eventId ?? (p.event as any)?.id
      if (eid) map[eid] = p
    })
    return map
  }, [futureChampionPredictions])

  useEffect(() => {
    if (isOpen && playerId && roomId) {
      loadPlayerData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId, roomId, isOpen])

  useEffect(() => {
    if (!isOpen || !roomId || !playerId || viewType !== 'games') return
    let cancelled = false
    setLoadingFutureChampion(true)
    Promise.all([
      futureChampionService.getEvents({ gameType: 'formula1', isActive: true }),
      futureChampionService.getPlayerPredictions(roomId, playerId),
    ])
      .then(([events, list]) => {
        if (cancelled) return
        setFutureChampionEvents(Array.isArray(events) ? events : [])
        setFutureChampionPredictions(Array.isArray(list) ? list : [])
      })
      .catch(() => {
        if (!cancelled) {
          setFutureChampionEvents([])
          setFutureChampionPredictions([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingFutureChampion(false)
      })
    return () => { cancelled = true }
  }, [isOpen, roomId, playerId, viewType])

  const loadPlayerData = async () => {
    try {
      setIsLoading(true)

      // Load player predictions
      const playerPredictions = await formula1Service.getPlayerPredictions(roomId, playerId)
      const predictionsArray = Array.isArray(playerPredictions) ? playerPredictions : []

      const [allRaces, stats] = await Promise.all([
        formula1Service.getRaces({ limit: 100 }),
        formula1Service.getPlayerStats(roomId, playerId).catch(() => null),
      ])
      const racesArray = Array.isArray(allRaces) ? allRaces : []

      // Create a map of raceId -> race for quick lookup
      const racesMap: Record<string, Formula1Race> = {}
      racesArray.forEach(race => {
        const raceId = race.id || race._id
        if (raceId) {
          racesMap[raceId] = race
        }
      })

      setPredictions(predictionsArray)
      setRaces(racesMap)
      setPlayerStats(stats)
    } catch (error) {
      console.error('Error loading player data:', error)
      toast.error('Failed to load player data')
      setPredictions([])
      setRaces({})
      setPlayerStats(null)
    } finally {
      setIsLoading(false)
    }
  }

  // Sort predictions: most recent race played first (by race date desc; completed races can be considered "played")
  const sortedPredictions = useMemo(() => {
    return [...predictions].sort((a, b) => {
      const raceA = races[a.raceId]
      const raceB = races[b.raceId]
      if (!raceA || !raceB) return 0
      const dateA = raceA.raceDate ? new Date(raceA.raceDate).getTime() : 0
      const dateB = raceB.raceDate ? new Date(raceB.raceDate).getTime() : 0
      return dateB - dateA
    })
  }, [predictions, races])

  // Calculate stats
  const stats = useMemo(() => {
    const totalPoints = predictions.reduce((sum, pred) => sum + (pred.totalPoints || 0), 0)
    const racesWithResults = predictions.filter(
      pred => races[pred.raceId]?.actualResults && Object.keys(races[pred.raceId].actualResults || {}).length > 0
    ).length
    const correctPredictions = predictions.filter(pred => {
      const race = races[pred.raceId]
      if (!race?.actualResults) return false

      // Check if any predicted position matches actual position
      let correctCount = 0
      Object.entries(pred.predictedPositions || {}).forEach(([driver, predictedPos]) => {
        const actualPos = race.actualResults?.[driver]
        if (actualPos === predictedPos) {
          correctCount++
        }
      })

      return correctCount > 0
    }).length

    const accuracyRatio = racesWithResults > 0
      ? Math.round((correctPredictions / racesWithResults) * 100)
      : 0

    return {
      totalPoints,
      racesParticipated: predictions.length,
      racesWithResults,
      correctPredictions,
      accuracyRatio,
    }
  }, [predictions, races])

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!isOpen) return null

  const modalContent = (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-2xl mx-2 sm:mx-4 glass-strong rounded-xl sm:rounded-2xl p-3 sm:p-5 lg:p-6 h-[80vh] max-h-[80vh] overflow-hidden flex flex-col min-h-0"
        style={{ minHeight: isLoading ? '320px' : undefined }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start gap-2 mb-6">
          <div className="min-w-0 flex-1">
            <h2 className="text-display-2 font-display font-bold text-foreground">
              🏎️ {player.name} - {viewType === 'games' ? 'Games' : 'Statistics'}
            </h2>
            {viewType === 'games' && (
              <div className="mt-2 flex flex-wrap gap-2 sm:gap-3">
                {loadingFutureChampion ? (
                  <span className="text-small text-muted-foreground">Loading…</span>
                ) : (
                  slotsToShow.map((event) => {
                    const pred = predictionByEventId[event.id ?? (event as any)._id]
                    const rawValue = pred ? pred.predictedChampion : null
                    const value = rawValue ? String(rawValue).replace(/^[\s"{]+|[\s"}]+$/g, '').trim() : 'Upcoming'
                    const hasResult = !!event.actualChampion
                    const points = pred?.points ?? 0
                    return (
                      <div
                        key={event.id ?? (event as any)._id}
                        className="min-w-0 rounded-md bg-muted/80 text-foreground border border-border/60 px-2.5 py-1.5 sm:px-3 sm:py-2"
                      >
                        <p className="text-xs sm:text-small font-medium text-muted-foreground mb-0.5">{event.name}</p>
                        <p className="text-small sm:text-body font-semibold text-foreground break-words">
                          <span className={value === 'Upcoming' ? 'text-muted-foreground italic' : 'text-primary'}>{value}</span>
                          {hasResult && points > 0 && pred && (
                            <span className="text-success text-xs sm:text-small ml-1">+{formatPoints(points, t)}</span>
                          )}
                        </p>
                      </div>
                    )
                  })
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all shrink-0" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
          </div>
        ) : viewType === 'stats' ? (
          <div className="space-y-6 overflow-y-auto overflow-x-hidden pr-1 min-h-0 flex-1">
            <div className="glass-card rounded-2xl p-6 border border-muted/20">
              <div className="text-center mb-4">
                <div className="text-4xl mb-2">🏎️</div>
                <h3 className="text-xl font-bold text-foreground mb-4">{player.name}</h3>
                <div className="bg-primary/10 rounded-xl px-4 py-2 inline-block">
                  <p className="text-primary font-bold text-display-2">
                    Total Points: {stats.totalPoints}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="glass-card rounded-xl p-4 border border-muted/20">
                <p className="text-body-lg text-muted-foreground mb-1">Races Participated</p>
                <p className="text-foreground font-bold text-display-2">{stats.racesParticipated}</p>
              </div>
              <div className="glass-card rounded-xl p-4 border border-muted/20">
                <p className="text-body-lg text-muted-foreground mb-1">Races with Results</p>
                <p className="text-foreground font-bold text-display-2">{stats.racesWithResults}</p>
              </div>
              <div className="glass-card rounded-xl p-4 border border-muted/20">
                <p className="text-body-lg text-muted-foreground mb-1">Correct Predictions</p>
                <p className="text-foreground font-bold text-display-2">{stats.correctPredictions}</p>
              </div>
              <div className="glass-card rounded-xl p-4 border border-muted/20">
                <p className="text-body-lg text-muted-foreground mb-1">Accuracy</p>
                <p className="text-foreground font-bold text-display-2">{stats.accuracyRatio}%</p>
              </div>
            </div>

            {playerStats?.accuracy && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="glass-card rounded-xl p-4 border border-muted/20">
                  <p className="text-body-lg text-muted-foreground mb-1">{t('formula1.exactSlotPct')}</p>
                  <p className="text-foreground font-bold text-display-2">{Math.round(playerStats.accuracy.exactSlotPct)}%</p>
                </div>
                <div className="glass-card rounded-xl p-4 border border-muted/20">
                  <p className="text-body-lg text-muted-foreground mb-1">{t('formula1.podiumPct')}</p>
                  <p className="text-foreground font-bold text-display-2">{Math.round(playerStats.accuracy.podiumPct)}%</p>
                </div>
                <div className="glass-card rounded-xl p-4 border border-muted/20">
                  <p className="text-body-lg text-muted-foreground mb-1">{t('formula1.p1Pct')}</p>
                  <p className="text-foreground font-bold text-display-2">{Math.round(playerStats.accuracy.p1Pct)}%</p>
                </div>
                <div className="glass-card rounded-xl p-4 border border-muted/20">
                  <p className="text-body-lg text-muted-foreground mb-1">{t('formula1.avgPointsPerScoredRace')}</p>
                  <p className="text-foreground font-bold text-display-2">{playerStats.accuracy.avgPointsPerScoredRace}</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col min-h-0 flex-1">
            {sortedPredictions.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🏎️</div>
                <p className="text-muted-foreground text-lg">No predictions yet</p>
              </div>
            ) : (
              <div className="space-y-4 overflow-y-auto overflow-x-hidden pr-1 min-h-0 flex-1">
                {sortedPredictions.map((prediction) => {
                  const race = races[prediction.raceId]
                  if (!race) return null

                  const raceDateStr = race.raceDate
                    ? new Date(race.raceDate).toLocaleDateString(undefined, { dateStyle: 'medium' })
                    : null

                  return (
                    <div
                      key={prediction.id || prediction._id}
                      className="glass-card rounded-xl p-4 border border-muted/20"
                    >
                      {/* Race header: city, country, date */}
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                        <div>
                          <h3 className="text-display-2 font-bold text-foreground">
                            Race {race.day}: {race.city}
                            {race.country ? `, ${race.country}` : ''}
                          </h3>
                          {raceDateStr && (
                            <p className="text-muted-foreground text-sm mt-0.5">
                              {raceDateStr}
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-warning font-bold text-display-2">
                            {formatPoints(prediction.totalPoints ?? 0, t)}
                          </p>
                          <p className="text-small text-muted-foreground capitalize">
                            {race.status || 'upcoming'}
                          </p>
                        </div>
                      </div>

                      {/* Positions 1–10 as blocks */}
                      <div className="mt-3 pt-3 border-t border-muted/20">
                        <p className="text-body-lg text-muted-foreground mb-3">Predictions (places 1–10):</p>
                        <Formula1PredictedGrid
                          predicted={prediction.hiddenUntilLock ? null : prediction.predictedPositions}
                          actual={race.actualResults}
                          live={race.liveResults}
                          hidden={Boolean(prediction.hiddenUntilLock)}
                          hiddenLabel={t('formula1.gridsHidden')}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default ViewPlayerFormula1StatsModal

